import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-white/40 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#0F4C3A] hover:text-[#0B3C2E] font-semibold transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            Terms of Use
          </h1>
          <p className="text-[#0F4C3A] font-semibold mb-8">Effective Date: May 2026</p>

          <div className="prose prose-lg max-w-none text-slate-600">
            <p className="text-slate-500 italic mb-8">
              This document constitutes an electronic record in terms of the Information Technology Act, 2000, and rules framed thereunder, as applicable and amended from time to time. This electronic record is generated by a computer system and does not require any physical or digital signatures.
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>1. Acceptance of Terms</h2>
              <p>
                By accessing, browsing, or utilizing the voice-first AI companion platform available at projectmaa.in (hereinafter referred to as the "Platform"), the user (hereinafter referred to as "User", "You", or "Your") agrees to be bound by these Terms of Use. If You do not agree to these terms, You must immediately cease the use of the Platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>2. Nature of Services and Medical Disclaimer</h2>
              <p className="mb-4">
                The Platform provides an Artificial Intelligence (AI) generated voice assistant designed to disseminate general informational content pertaining to maternal health, pregnancy, and neonatal care, curated from the medical expertise of registered medical practitioners, including Dr. Kafeel Khan and Dr. Shabista Khan.
              </p>
              
              <div className="bg-[#FF6B6B]/10 border-l-4 border-[#FF6B6B] p-4 rounded-r-lg mb-4">
                <h3 className="font-bold text-[#FF6B6B] mb-2">DISCLAIMER OF MEDICAL LIABILITY:</h3>
                <p className="text-slate-700 font-semibold">
                  THE PLATFORM AND ITS AI AGENT DO NOT PRACTICE MEDICINE OR PROVIDE HEALTHCARE SERVICES, CLINICAL ADVICE, DIAGNOSIS, OR TREATMENT.
                </p>
              </div>
              
              <p>
                The content provided by the Platform is strictly for informational and educational purposes and shall not be construed as a substitute for professional medical advice from a Registered Medical Practitioner (RMP) under the National Medical Commission Act, 2019. In the event of a medical emergency, the User must immediately contact local emergency healthcare services or visit the nearest clinical establishment. Reliance on any information provided by the Platform is solely at the User's own risk.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>3. User Obligations and Lawful Use</h2>
              <p className="mb-4">
                The User covenants and agrees to utilize the Platform in compliance with all applicable laws, including Section 43 and Section 66 of the Information Technology Act, 2000. The User shall not:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the Platform to transmit any defamatory, obscene, harassing, or unlawful material.</li>
                <li>Attempt to reverse-engineer, decompile, or compromise the integrity or security of the Platform's AI architecture or underlying voice-processing technologies.</li>
                <li>Provide intentionally false or misleading health information that may disrupt the functionality of the AI agent.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>4. Intellectual Property Rights</h2>
              <p>
                All intellectual property associated with the Platform, including but not limited to source code, algorithms, conversational scripts, audio outputs, trademarks, and user interfaces, are the exclusive property of Project Maa and its licensors. No right, title, or interest in any downloaded materials or software is transferred to the User as a result of accessing the Platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>5. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by applicable Indian law, Project Maa, its founders, affiliates, medical advisors, and third-party service providers shall not be liable for any direct, indirect, incidental, special, consequential, or exemplary damages, including but not limited to damages for loss of life, personal injury, emotional distress, or data loss, arising out of or in connection with the use or inability to use the Platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>6. Indemnification</h2>
              <p>
                The User agrees to indemnify, defend, and hold harmless Project Maa, its officers, directors, employees, and medical consultants from any claims, liabilities, damages, losses, or expenses (including reasonable legal fees) arising out of the User's breach of these Terms or misuse of the Platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>7. Governing Law and Dispute Resolution</h2>
              <p>
                These Terms of Use shall be governed by and construed strictly in accordance with the laws of the Republic of India. Any legal suit, action, or proceeding arising out of or relating to these terms shall be instituted exclusively in the competent courts located in New Delhi, Delhi, India, and the User irrevocably submits to the exclusive jurisdiction of such courts.
              </p>
            </section>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default TermsOfUse;
